import { useState } from 'react';
import type { MediaFilter, MediaItem } from '@wallflower/shared';
import { FAILURE_MESSAGES } from '@wallflower/shared';

interface Props {
  media: MediaItem[];
  onToggleHidden: (item: MediaItem) => void;
  onDelete: (item: MediaItem) => void;
}

const FILTERS: { value: MediaFilter; label: string }[] = [
  { value: 'all', label: 'Everything' },
  { value: 'visible', label: 'On screen' },
  { value: 'hidden', label: 'Hidden' },
  { value: 'failed', label: 'Didn’t work' },
];

export default function MediaGrid({ media, onToggleHidden, onDelete }: Props) {
  const [filter, setFilter] = useState<MediaFilter>('all');
  const [open, setOpen] = useState<MediaItem | null>(null);

  const shown = media.filter((item) => {
    if (filter === 'visible') return item.status === 'ready' && !item.hidden;
    if (filter === 'hidden') return item.hidden;
    if (filter === 'failed') return item.status === 'failed';
    return true;
  });

  const counts = {
    hidden: media.filter((m) => m.hidden).length,
    failed: media.filter((m) => m.status === 'failed').length,
  };

  return (
    <section className="panel p-5">
      <div className="flex flex-wrap items-baseline justify-between gap-3">
        <h2 className="eyebrow">What guests have sent</h2>
        <p className="text-xs text-muted">
          {media.length} item{media.length === 1 ? '' : 's'}
          {counts.hidden > 0 ? ` · ${counts.hidden} hidden` : ''}
          {counts.failed > 0 ? ` · ${counts.failed} failed` : ''}
        </p>
      </div>

      <div className="mt-4 flex flex-wrap gap-2">
        {FILTERS.map((option) => (
          <button
            key={option.value}
            type="button"
            onClick={() => setFilter(option.value)}
            aria-pressed={filter === option.value}
            className="rounded-full border px-3 py-1.5 text-xs"
            style={{
              borderColor: filter === option.value ? 'var(--color-petal)' : 'var(--color-edge)',
              color: filter === option.value ? 'var(--color-chalk)' : 'var(--color-muted)',
            }}
          >
            {option.label}
          </button>
        ))}
      </div>

      {shown.length === 0 ? (
        <p className="mt-8 mb-4 text-center text-sm text-muted">
          {media.length === 0
            ? 'Nothing yet. The first scan usually happens within a minute of the QR going up.'
            : 'Nothing in this view.'}
        </p>
      ) : (
        <ul className="mt-4 grid grid-cols-3 gap-2 sm:grid-cols-4 lg:grid-cols-5">
          {shown.map((item) => (
            <li key={item.id}>
              <button
                type="button"
                onClick={() => setOpen(item)}
                className="relative block aspect-square w-full overflow-hidden rounded-lg border border-edge bg-dusk-deep"
              >
                {item.thumbUrl ? (
                  <img
                    src={item.thumbUrl}
                    alt=""
                    loading="lazy"
                    className="h-full w-full object-cover"
                    style={{ opacity: item.hidden ? 0.25 : 1 }}
                  />
                ) : (
                  <span className="grid h-full place-items-center text-[10px] text-muted">
                    {item.status === 'failed' ? 'failed' : 'processing'}
                  </span>
                )}

                {item.kind === 'video' && item.status === 'ready' ? (
                  <span className="absolute bottom-1 left-1 rounded bg-black/70 px-1.5 py-0.5 text-[10px] text-chalk">
                    {Math.round((item.durationMs ?? 0) / 1000)}s
                  </span>
                ) : null}

                {item.hidden ? (
                  <span className="absolute inset-x-1 top-1 rounded bg-dusk/90 px-1 py-0.5 text-center text-[10px] text-sulphur">
                    Hidden
                  </span>
                ) : null}
                {item.status === 'processing' ? (
                  <span className="absolute inset-x-1 top-1 rounded bg-dusk/90 px-1 py-0.5 text-center text-[10px] text-muted">
                    Processing
                  </span>
                ) : null}
              </button>
            </li>
          ))}
        </ul>
      )}

      {open ? (
        <Lightbox
          item={media.find((m) => m.id === open.id) ?? open}
          onClose={() => setOpen(null)}
          onToggleHidden={onToggleHidden}
          onDelete={(item) => {
            onDelete(item);
            setOpen(null);
          }}
        />
      ) : null}
    </section>
  );
}

function Lightbox({
  item,
  onClose,
  onToggleHidden,
  onDelete,
}: {
  item: MediaItem;
  onClose: () => void;
  onToggleHidden: (item: MediaItem) => void;
  onDelete: (item: MediaItem) => void;
}) {
  const [confirming, setConfirming] = useState(false);

  return (
    <div
      className="fixed inset-0 z-50 grid place-items-center bg-dusk-deep/90 p-4 backdrop-blur"
      role="dialog"
      aria-modal="true"
      onClick={onClose}
    >
      <div
        className="panel w-full max-w-lg overflow-hidden"
        onClick={(event) => event.stopPropagation()}
      >
        <div className="grid max-h-[55dvh] place-items-center bg-black">
          {item.status === 'ready' ? (
            item.kind === 'video' ? (
              <video src={item.url} controls playsInline className="max-h-[55dvh] w-full" />
            ) : (
              <img src={item.url} alt="" className="max-h-[55dvh] w-full object-contain" />
            )
          ) : (
            <p className="p-10 text-center text-sm text-muted">
              {item.status === 'failed'
                ? (item.failureReason && FAILURE_MESSAGES[item.failureReason]) ||
                  'This one could not be used.'
                : 'Still processing…'}
            </p>
          )}
        </div>

        <div className="p-4">
          <p className="text-sm text-chalk">
            {item.uploaderName ?? 'Someone'} · {new Date(item.createdAt).toLocaleTimeString()}
          </p>

          <div className="mt-4 flex flex-wrap gap-2">
            {item.status === 'ready' ? (
              <button className="btn btn-primary" onClick={() => onToggleHidden(item)}>
                {item.hidden ? 'Put back on screen' : 'Hide from screen'}
              </button>
            ) : null}
            <button className="btn btn-ghost" onClick={onClose}>
              Close
            </button>
            {confirming ? (
              <button
                className="btn btn-ghost"
                style={{ borderColor: 'var(--color-danger)', color: 'var(--color-danger)' }}
                onClick={() => onDelete(item)}
              >
                Delete for good
              </button>
            ) : (
              <button
                className="btn btn-ghost ml-auto"
                style={{ color: 'var(--color-danger)' }}
                onClick={() => setConfirming(true)}
              >
                Delete
              </button>
            )}
          </div>
          {confirming ? (
            <p className="mt-3 text-xs text-muted">
              Deleting removes the file from the server. Hiding is enough to get it off the screen.
            </p>
          ) : null}
        </div>
      </div>
    </div>
  );
}
